import { useQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { MINUTE_IN_MS } from '@/modules/shared/constants/time'
import { CompanyDetailsModel } from '@/app/models/companyDetailsModel'
import { CompanyType } from '@/app/constants/companyType'

type Props = {
  includeDeleted?: boolean
}

/**
 * Fetches companies from the config API.
 * Flattens parent companies and their child companies into a single array.
 */
export function useQueryCompanies({ includeDeleted = false }: Props = {}) {
  return useQuery<CompanyDetailsModel[]>({
    queryKey: ['config.companies', includeDeleted],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `/config-api/company/list?&includeDeleted=${includeDeleted}`,
      )

      // Response structure: CompanyListItem[] (direct array from server)
      // id is a number in the response, needs to be converted to string
      const companiesData = Array.isArray(data) ? data : []
      const companies: CompanyDetailsModel[] = []

      // Flatten companies and their child companies
      for (const company of companiesData) {
        // Map CompanyListItem to CompanyDetailsModel
        // id is a number, convert to string
        // The umbrella relation survives flattening so a child can point at the service
        // account that bills it. Roots carry it too: a member sees only the children.
        const tenant: CompanyDetailsModel | undefined = company.tenant
          ? {
              id: String(company.tenant.id),
              companyName: company.tenant.companyName,
              invoiceCompanyName: company.tenant.invoiceCompanyName,
              type: company.tenant.type ? CompanyType.SERVICE : CompanyType.NORMAL,
              hasDatabase: company.tenant.hasDatabase,
            }
          : undefined
        const companyModel: CompanyDetailsModel = {
          id: String(company.id),
          tenantId: company.tenantId ? String(company.tenantId) : undefined,
          tenant,
          companyName: company.companyName,
          invoiceCompanyName: company.invoiceCompanyName,
          uniqueIdentifier: company.uniqueIdentifier,
          // `type` arrives as the backend's IsServiceCompany bool; the model
          // holds the string enum, so map it rather than writing 1/0 (which
          // never matches CompanyType.SERVICE downstream).
          type: company.type ? CompanyType.SERVICE : CompanyType.NORMAL,
          isValid: company.isValid,
          hasDatabase: company.hasDatabase,
          isUnderServiceAccount: company.isUnderServiceAccount === true,
          serviceAccountName: company.serviceAccountName ?? undefined,
          accountingOnly: company.accountingOnly,
          createdDate: company.createdDate,
          isActive: company.isActive,
          deletedDate: company.deletedDate || company.DeletedDate, // Map from backend (camelCase or PascalCase)
          // isDeleted is derived from deletedDate (if deletedDate is not null, then it's deleted)
        }
        companies.push(companyModel)

        // Add child companies to the flattened list
        if (company.childCompanies != null && company.childCompanies.length > 0) {
          for (const childCompany of company.childCompanies) {
            companies.push({
              id: String(childCompany.id),
              tenantId: companyModel.id,
              tenant: companyModel,
              companyName: childCompany.companyName,
              invoiceCompanyName: childCompany.invoiceCompanyName,
              uniqueIdentifier: childCompany.uniqueIdentifier,
              type: childCompany.type ? CompanyType.SERVICE : CompanyType.NORMAL,
              isValid: childCompany.isValid,
              hasDatabase: childCompany.hasDatabase,
              isUnderServiceAccount: childCompany.isUnderServiceAccount === true,
              serviceAccountName: childCompany.serviceAccountName ?? undefined,
              accountingOnly: childCompany.accountingOnly,
              createdDate: childCompany.createdDate,
              isActive: childCompany.isActive,
              deletedDate: childCompany.deletedDate || childCompany.DeletedDate, // Map from backend (camelCase or PascalCase)
              // isDeleted is derived from deletedDate (if deletedDate is not null, then it's deleted)
            })
          }
        }
      }

      return companies
    },
    staleTime: MINUTE_IN_MS * 5, // cache for 5 minutes
  })
}

